/**
 * `/settings/automation` — rules, occasions, and the DRY RUN.
 *
 * THE DRY RUN IS THE POINT OF THIS SCREEN. An automation rule sends messages to
 * real doctors without anybody pressing anything, and the only honest way to
 * review one is to ask "who would get what, on this date?" and read the answer.
 * It writes nothing.
 *
 * Occasions are stored in JALALI month/day because that is what the date
 * MEANS: روز پزشک is 1 شهریور every year, and its Gregorian equivalent moves.
 */
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Spinner,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from '@meshhub/design-tokens/ui';
import { Play, Plus } from 'lucide-react';
import React, { useState } from 'react';
import {
  FormDialog,
  MutationError,
  SelectField,
  TextField,
} from '../../components/FormKit.web.jsx';
import { SettingsPage, SystemLock } from '../../components/settings/SettingsKit.web.jsx';
import { PERSIAN_MONTHS, formatCount } from '../../lib/format.js';
import {
  AUTOMATION_DRY_RUN_QUERY,
  AUTOMATION_QUERY,
  CREATE_AUTOMATION_RULE,
  CREATE_OCCASION,
  TEMPLATES_QUERY,
  UPDATE_AUTOMATION_RULE,
} from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

const TRIGGER_LABELS = {
  BIRTHDAY: S.automation.triggerBirthday,
  OCCASION: S.automation.triggerOccasion,
  STATUS_CHANGED: S.automation.triggerStatusChanged,
  ISSUE_RESOLVED: S.automation.triggerIssueResolved,
};

export function AutomationScreen() {
  const { data, loading, refetch } = useQuery(AUTOMATION_QUERY, {
    variables: { includeDisabled: true },
  });
  const { data: templateData } = useQuery(TEMPLATES_QUERY, {
    variables: { includeInactive: false },
    errorPolicy: 'ignore',
  });
  const [toggle, toggleState] = useMutation(UPDATE_AUTOMATION_RULE, {
    onCompleted: () => refetch(),
  });
  const [creatingRule, setCreatingRule] = useState(false);
  const [creatingOccasion, setCreatingOccasion] = useState(false);

  const rules = data?.automationRules ?? [];
  const occasions = data?.occasions ?? [];

  return (
    <SettingsPage title={S.automation.title}>
      <MutationError error={toggleState.error} />

      <Card className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-md font-bold">{S.automation.rules}</h2>
          <Button variant="primary" onClick={() => setCreatingRule(true)}>
            <Plus size={13} aria-hidden="true" />
            {S.automation.newRule}
          </Button>
        </div>
        {loading && rules.length === 0 && <Spinner />}
        {!loading && rules.length === 0 && <EmptyState title={S.app.notFound} />}
        {rules.length > 0 && (
          <Table>
            <THead>
              <TR>
                <TH>{S.automation.ruleName}</TH>
                <TH>{S.automation.trigger}</TH>
                <TH>{S.automation.sendTime}</TH>
                <TH>{S.automation.enabled}</TH>
              </TR>
            </THead>
            <TBody>
              {rules.map((rule) => (
                <TR key={rule.id}>
                  <TD className="font-medium">{rule.name}</TD>
                  <TD>
                    <Badge className="bg-surface-hover">
                      {TRIGGER_LABELS[rule.trigger] ?? rule.trigger}
                    </Badge>
                  </TD>
                  <TD dir="ltr" className="text-start tabular-nums">
                    {rule.sendTimeLocal || S.app.none}
                  </TD>
                  <TD>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="accent-primary"
                        checked={rule.enabled}
                        onChange={(event) =>
                          toggle({
                            variables: { id: rule.id, enabled: event.target.checked },
                          })
                        }
                      />
                      {rule.enabled ? S.app.active : S.app.inactive}
                    </label>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <Card className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-md font-bold">{S.automation.occasions}</h2>
          <Button variant="primary" onClick={() => setCreatingOccasion(true)}>
            <Plus size={13} aria-hidden="true" />
            {S.automation.newOccasion}
          </Button>
        </div>
        <Table>
          <THead>
            <TR>
              <TH>{S.automation.occasionLabel}</TH>
              <TH>{S.automation.occasionCode}</TH>
              <TH>{S.automation.jalaliMonth}</TH>
              <TH>{S.automation.jalaliDay}</TH>
              <TH />
            </TR>
          </THead>
          <TBody>
            {occasions.map((occasion) => (
              <TR key={occasion.id}>
                <TD className="font-medium">{occasion.labelFa}</TD>
                <TD dir="ltr" className="text-start text-text-muted">
                  {occasion.code}
                </TD>
                <TD>{PERSIAN_MONTHS[occasion.jalaliMonth - 1]}</TD>
                <TD className="tabular-nums">{formatCount(occasion.jalaliDay)}</TD>
                <TD>
                  <SystemLock isSystem={occasion.isSystem} />
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </Card>

      <DryRunCard />

      <RuleDialog
        open={creatingRule}
        onOpenChange={setCreatingRule}
        templates={templateData?.messageTemplates ?? []}
        occasions={occasions}
        onCreated={refetch}
      />
      <OccasionDialog
        open={creatingOccasion}
        onOpenChange={setCreatingOccasion}
        onCreated={refetch}
      />
    </SettingsPage>
  );
}

function DryRunCard() {
  const [date, setDate] = useState('');
  const [run, { data, loading, error }] = useLazyQuery(AUTOMATION_DRY_RUN_QUERY, {
    fetchPolicy: 'network-only',
  });
  const entries = data?.automationDryRun ?? [];

  return (
    <Card className="flex flex-col gap-3">
      <h2 className="text-md font-bold">{S.automation.dryRun}</h2>
      <p className="text-sm text-text-muted">{S.automation.dryRunHint}</p>
      <div className="flex items-end gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-text-muted">{S.automation.dryRunDate}</span>
          <input
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="h-control rounded border border-border bg-bg px-3 text-base
                       outline-none focus:border-primary"
          />
        </label>
        <Button variant="primary" onClick={() => run({ variables: { localDate: date || null } })}>
          <Play size={13} aria-hidden="true" />
          {S.automation.dryRun}
        </Button>
      </div>
      <MutationError error={error} />
      {loading && <Spinner />}
      {!loading && data && entries.length === 0 && (
        <EmptyState title={S.automation.dryRunEmpty} />
      )}
      {entries.length > 0 && (
        <Table>
          <THead>
            <TR>
              <TH>{S.messaging.recipient}</TH>
              <TH>{S.automation.ruleName}</TH>
              <TH>{S.messaging.body}</TH>
            </TR>
          </THead>
          <TBody>
            {entries.map((entry) => (
              <TR key={`${entry.ruleId}:${entry.contactId}`}>
                <TD className="font-medium">{entry.displayName}</TD>
                <TD className="text-text-muted">
                  {entry.ruleName} · {entry.reason}
                </TD>
                <TD className="text-text-muted">{entry.renderedBody}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}

function RuleDialog({ open, onOpenChange, templates, occasions, onCreated }) {
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState('BIRTHDAY');
  const [templateId, setTemplateId] = useState('');
  const [occasionCode, setOccasionCode] = useState('');
  const [toStatus, setToStatus] = useState('SUBSCRIBER');
  const [create, { loading, error }] = useMutation(CREATE_AUTOMATION_RULE);

  const config = () => {
    if (trigger === 'OCCASION') return JSON.stringify({ occasion_code: occasionCode });
    if (trigger === 'STATUS_CHANGED') return JSON.stringify({ to_status: toStatus });
    return '{}';
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={S.automation.newRule}
      submitLabel={S.app.create}
      busy={loading}
      error={error}
      canSubmit={Boolean(name.trim() && templateId)}
      onSubmit={async () => {
        await create({
          variables: {
            name: name.trim(),
            trigger,
            templateId,
            triggerConfigJson: config(),
          },
        });
        setName('');
        onOpenChange(false);
        onCreated();
      }}
    >
      <TextField
        label={S.automation.ruleName}
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <SelectField
        label={S.automation.trigger}
        value={trigger}
        onChange={(event) => setTrigger(event.target.value)}
      >
        {Object.entries(TRIGGER_LABELS).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </SelectField>
      {trigger === 'OCCASION' && (
        <SelectField
          label={S.automation.occasions}
          value={occasionCode}
          onChange={(event) => setOccasionCode(event.target.value)}
        >
          <option value="">{S.app.none}</option>
          {occasions.map((occasion) => (
            <option key={occasion.id} value={occasion.code}>
              {occasion.labelFa}
            </option>
          ))}
        </SelectField>
      )}
      {trigger === 'STATUS_CHANGED' && (
        <SelectField
          label={S.automation.toStatus}
          value={toStatus}
          onChange={(event) => setToStatus(event.target.value)}
        >
          {['UNKNOWN', 'INTERESTED', 'CONSULTING', 'SUBSCRIBER'].map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </SelectField>
      )}
      <SelectField
        label={S.messaging.template}
        value={templateId}
        onChange={(event) => setTemplateId(event.target.value)}
      >
        <option value="">{S.app.none}</option>
        {templates.map((template) => (
          <option key={template.id} value={template.id}>
            {template.name}
          </option>
        ))}
      </SelectField>
    </FormDialog>
  );
}

function OccasionDialog({ open, onOpenChange, onCreated }) {
  const [code, setCode] = useState('');
  const [labelFa, setLabelFa] = useState('');
  const [month, setMonth] = useState(1);
  const [day, setDay] = useState(1);
  const [create, { loading, error }] = useMutation(CREATE_OCCASION);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={S.automation.newOccasion}
      submitLabel={S.app.create}
      busy={loading}
      error={error}
      canSubmit={Boolean(code.trim() && labelFa.trim())}
      onSubmit={async () => {
        await create({
          variables: {
            code: code.trim().toUpperCase(),
            labelFa: labelFa.trim(),
            jalaliMonth: Number(month),
            jalaliDay: Number(day),
          },
        });
        setCode('');
        setLabelFa('');
        onOpenChange(false);
        onCreated();
      }}
    >
      <TextField
        label={S.automation.occasionCode}
        dir="ltr"
        value={code}
        onChange={(event) => setCode(event.target.value)}
      />
      <TextField
        label={S.automation.occasionLabel}
        value={labelFa}
        onChange={(event) => setLabelFa(event.target.value)}
      />
      <SelectField
        label={S.automation.jalaliMonth}
        value={month}
        onChange={(event) => setMonth(event.target.value)}
      >
        {PERSIAN_MONTHS.map((label, index) => (
          <option key={label} value={index + 1}>
            {label}
          </option>
        ))}
      </SelectField>
      <SelectField
        label={S.automation.jalaliDay}
        value={day}
        onChange={(event) => setDay(event.target.value)}
      >
        {Array.from({ length: 31 }, (_, i) => i + 1).map((value) => (
          <option key={value} value={value}>
            {formatCount(value)}
          </option>
        ))}
      </SelectField>
    </FormDialog>
  );
}
